// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-09-05

import * as React from "react";

import { IBaseProps } from "lib/data/interfaces";
import { ICustomPermission } from "../PermissionManager";

import { Form, Select, Switch, Table } from "antd";
import { FormComponentProps } from "antd/lib/form";
import { ComponentTemplate, Permission, User } from "lib/data/model";

import Search from "antd/lib/input/Search";
import { ColumnProps } from "antd/lib/table";
import { Guid } from "guid-typescript";
import { Right, UserStatus } from "lib/data/model/enumerations";
import { FuturePermission } from "lib/data/model/security/FuturePermission";
import { MultipleRecordPermission } from "lib/data/model/security/MultipleRecordPermission";
import { SingleRecordPermission } from "lib/data/model/security/SingleRecordPermission";
import cloneDeep from "lodash/cloneDeep";
import "./PermissionManagerForm.less";
import { AdminPagePermission } from "lib/data/model/security/AdminPagePermission";

interface IPermissionManagerFormProps extends IBaseProps, FormComponentProps {

	permissionToLoad: ICustomPermission | null;
	currentUserPermissions: Permission[];
	targetUserPermissions: Permission[];
	permittedUsers: User[];
	onSave: (permission: ICustomPermission[]) => void;
}

interface IPermissionManagerFormState {

	tableSearchText: string;
	selectedRecords: string[];
}

const formFields: {
	[field: string]: {
		label: React.ReactNode;
		name: string;
	};
} = {
	type: {
		label: "Record Type",
		name: "type"
	},
	right: {
		label: "Right",
		name: "right"
	},
	canEditFutureRecords: {
		label: "Allow edit on created users",
		name: "canEditFutureRecords"
	}
};

export class PermissionManagerDefintion extends React.Component<IPermissionManagerFormProps, IPermissionManagerFormState> {

	public constructor(props: IPermissionManagerFormProps) {

		super(props);

		const selectedRecords: string[] = [];

		if (props.permissionToLoad !== null) {

			if ((props.permissionToLoad.permission as SingleRecordPermission).recordId !== undefined) {

				selectedRecords.push((props.permissionToLoad.permission as SingleRecordPermission).recordId.toString());
			}
			if ((props.permissionToLoad.permission as MultipleRecordPermission).recordIds !== undefined) {

				selectedRecords.push(...(props.permissionToLoad.permission as MultipleRecordPermission).recordIds.toString());
			}
		}

		this.state = {
			tableSearchText: "",
			selectedRecords
		};
	}

	public saveForm = async () => {

		let permission: Permission;
		const form = this.props.form;

		this.props.form.validateFields();
		const errors = form.getFieldsError();

		if (errors[formFields.type.name] === undefined && errors[formFields.right.name] === undefined) {

			const typeValue = (form.getFieldValue(formFields.type.name) as string).toLowerCase() === "all" ? undefined : form.getFieldValue(formFields.type.name);

			if (this.state.selectedRecords.length > 0) {

				const selectedRecords: string[] = [];

				// Only include the records that were selected if the Right = Update
				if (form.getFieldValue(formFields.right.name) === Right.Update) {

					selectedRecords.push(...this.state.selectedRecords);
				}

				permission = new MultipleRecordPermission(
					form.getFieldValue(formFields.right.name),
					typeValue,
					selectedRecords.map((id) => +id)
				);
			}
			else {

				permission = new Permission(
					form.getFieldValue(formFields.right.name),
					typeValue
				);
			}

			const customPermission: ICustomPermission = {
				permissionId: this.props.permissionToLoad !== null ? this.props.permissionToLoad.permissionId : Guid.create(),
				permission
			};

			const permissionsToAdd = [customPermission];

			if (
				form.getFieldValue(formFields.canEditFutureRecords.name) === true
				&& form.getFieldValue(formFields.right.name) === Right.Create
			) {
				permissionsToAdd.push({
					permissionId: Guid.create(),
					permission: new FuturePermission(
						Right.Update,
						form.getFieldValue(formFields.type.name),
						Right.Create
					)
				});
			}

			this.props.onSave(permissionsToAdd);
		}
	}

	public resetForm = () => {

		this.props.form.resetFields();
		this.setState({
			tableSearchText: "",
			selectedRecords: []
		});
	}

	public render() {

		const { getFieldDecorator } = this.props.form;

		const typeSelect = (
			<Select allowClear={true}>
				{this.getAvailableRecordTypeOptions()}
			</Select>
		);

		const rightSelect = (
			<Select
				disabled={
					this.props.form.getFieldValue(formFields.type.name) === undefined
					&& (this.props.permissionToLoad === null)
				}
			>
				{/* <Select.Option value={Right.Create}>Create</Select.Option>
				<Select.Option value={Right.Update}>Update</Select.Option> */}
				{this.getAvailableRecordRightOptions()}
			</Select>
		);

		const userTableColumns: Array<ColumnProps<User>> = [
			{
				title: "Name",
				key: "manager-table-name-col",
				render: (user: User) => `${user.firstName} ${user.lastName}`
			},
			{
				title: "Email",
				dataIndex: "email"
			},
			{
				title: "Status",
				key: "manager-table-status-col",
				render: (user: User) => this.translateUserStatus(user.status)
			},
		];

		const className = `permission-manager-form${this.props.className ? ` ${this.props.className}` : ""}`;

		return (
			<section className={className}>
				<Form>
					<Form.Item label={formFields.type.label}>
						{
							getFieldDecorator(formFields.type.name, {
								initialValue: this.props.permissionToLoad ? this.props.permissionToLoad.permission.recordType : undefined,
								rules: [
									{
										required: true,
										message: "You must specify a type"
									}
								]
							})(typeSelect)
						}
					</Form.Item>
					<Form.Item label={formFields.right.label}>
						{
							getFieldDecorator(formFields.right.name, {
								initialValue: this.props.permissionToLoad ? this.props.permissionToLoad.permission.right : undefined,
								rules: [
									{
										required: true,
										message: "You must specify a right"
									}
								]
							})(rightSelect)
						}
					</Form.Item>
					{
						this.props.form.getFieldValue(formFields.type.name) === User.$type
						&& this.props.form.getFieldValue(formFields.right.name) === Right.Update
						?
						<Form.Item>
							<div className="permission-manager-table-actions">
								<div>
									For Record(s):
								</div>
								<Search
									placeholder="Search by first and last name"
									onChange={this.onTableSearchChange}
									style={{ width: 250 }}
									size="small"
								/>
							</div>
							<Table
								className="permission-manager-table"
								dataSource={this.getRecordsInTable()}
								columns={userTableColumns}
								size="small"
								rowKey={(record) => record.id!.toString()}
								rowSelection={{
									selectedRowKeys: this.state.selectedRecords,
									onSelect: this.onTableRecordSelect
								}}
							/>
						</Form.Item>
						:
						null
					}
					{
						this.props.form.getFieldValue(formFields.right.name) === Right.Create && this.props.permissionToLoad === null
						?
						<Form.Item label={formFields.canEditFutureRecords.label}>
							{
								getFieldDecorator(formFields.canEditFutureRecords.name, {
									valuePropName: "checked"
								})(<Switch />)
							}
						</Form.Item>
						:
						null
					}
				</Form>
			</section>
		);
	}

	private onTableRecordSelect = <T extends {$type: string}>(record: T, isSelected: boolean) => {

		const permittedUsers = cloneDeep(this.state.selectedRecords);

		switch (record.$type) {

			/**
			 * User
			 */
			case User.$type:
				const recordSelected = (record as unknown as User);

				if (recordSelected.id !== undefined) {

					if (isSelected === true) {

						permittedUsers.push(recordSelected.id.toString());
					}
					else if (isSelected === false) {

						const indexOfRecord = permittedUsers.findIndex((id) => id === (recordSelected.id && recordSelected.id.toString()));

						if (indexOfRecord > -1) {

							permittedUsers.splice(indexOfRecord, 1);
						}
					}
					this.setState({
						selectedRecords: permittedUsers
					});
				}

				return;
		}
	}

	private onTableSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		this.setState({tableSearchText: event.target.value});
	}

	private getRecordsInTable = (): User[] => {

		return this.props.permittedUsers.filter((user) => {

			if (
				user.id !== undefined
				&& (
					new RegExp(`.*${this.state.tableSearchText}.*`, "i").test(`${user.firstName} ${user.lastName}`)
					|| this.state.selectedRecords.findIndex((record) => record === user.id!.toString()) > -1
				)
			) {
				return true;
			}

			return false;
		});
	}

	private translateUserStatus = (status: UserStatus): string => {

		for (const key in UserStatus) {

			if (UserStatus[key].toString() === status.toString()) {

				return key;
			}
		}

		return "";
	}

	private getAvailableRecordRightOptions = (): React.ReactNode[] => {

		const options: React.ReactNode[] = [];

		const selectedTypeFormValue = this.props.form.getFieldValue(formFields.type.name) as string | undefined;

		if (selectedTypeFormValue !== undefined) {

			const selectedType = selectedTypeFormValue.toLowerCase() === "all" ? undefined : selectedTypeFormValue;

			// We only want the user to be able to select "Read" for admin page permission
			if (selectedType === AdminPagePermission.$type) {
				options.push(<Select.Option key="record-right-read-option" value={Right.Read}>Read</Select.Option>);

				return options;
			}

			if (this.props.targetUserPermissions.find((permission) => permission.right === Right.Create && permission.recordType === selectedType) === undefined) {
				options.push(<Select.Option key="record-right-create-option" value={Right.Create}>Create</Select.Option>);
			}

			if (this.props.targetUserPermissions.find((permission) => permission.right === Right.Update && permission.recordType === selectedType) === undefined) {
				options.push(<Select.Option key="record-right-update-option" value={Right.Update}>Update</Select.Option>);
			}
		}

		return options;
	}

	private getAvailableRecordTypeOptions = (): React.ReactNode[] => {

		let options: React.ReactNode[] = [];

		const availableRecordType = {
			all: this.props.currentUserPermissions.findIndex((permission) => permission.recordType === undefined) > -1,
			user: this.props.currentUserPermissions.findIndex((permission) => permission.recordType === User.$type) > -1,
			template: this.props.currentUserPermissions.findIndex((permission) => permission.recordType === ComponentTemplate.$type) > -1,
			adminPage: this.props.currentUserPermissions.findIndex((permission) => permission.recordType === AdminPagePermission.$type) > -1
		};

		if (availableRecordType.all) {
			options.push(...[
				this.areRightsAvailable("all") ? <Select.Option key="record-type-all-option" value={"All"}>All</Select.Option> : null,
				this.areRightsAvailable("user") ? <Select.Option key="record-type-user-option" value={User.$type}>User</Select.Option> : null,
				this.areRightsAvailable("component-template") ? <Select.Option key="record-type-template-option" value={ComponentTemplate.$type}>Template</Select.Option> : null,
				this.areRightsAvailable("admin-page") ? <Select.Option key="record-type-admin-page-option" value={AdminPagePermission.$type}>Access To Admin Pages</Select.Option> : null
			]);
			options = options.filter((option) => option !== null);
		}
		else {

			if (availableRecordType.user) {
				options.push(
					this.areRightsAvailable("user") ? <Select.Option key="record-type-user-option" value={User.$type}>User</Select.Option> : null
				);
			}
			if (availableRecordType.template) {
				options.push(
					this.areRightsAvailable("component-template") ? <Select.Option key="record-type-template-option" value={ComponentTemplate.$type}>Template</Select.Option> : null
				);
			}
			if (availableRecordType.adminPage) {
				options.push(
					this.areRightsAvailable("admin-page") ? <Select.Option key="record-type-admin-page-option" value={AdminPagePermission.$type}>Access To Admin Pages</Select.Option> : null
				);
			}

			options = options.filter((option) => option !== null);
		}

		return options;
	}

	private areRightsAvailable = (type: "all" | "user" | "component-template" | "admin-page"): boolean => {

		let actualRecordType: string | undefined;

		switch (type) {

			case "all":
				actualRecordType = undefined;
				break;
			case "user":
				actualRecordType = User.$type;
				break;
			case "component-template":
				actualRecordType = ComponentTemplate.$type;
				break;
			case "admin-page":
				actualRecordType = AdminPagePermission.$type;
				break;
			default:
				throw new Error("The specified type is not handled");
		}

		if (
			this.props.targetUserPermissions.find((permission) => permission.right === Right.Create && permission.recordType === actualRecordType) !== undefined
			&& this.props.targetUserPermissions.find((permission) => permission.right === Right.Update && permission.recordType === actualRecordType) !== undefined
		) {
			return false;
		}

		return true;
	}
}

export const PermissionManagerForm = Form.create<IPermissionManagerFormProps>()(PermissionManagerDefintion);
